<app-admin-header [header]="'Order Details'" [links]="[]" [breadcrumbs]="breadcrumbs" [stats]="[]">
</app-admin-header>

<div class="container-fluid p-4" *ngIf="order">
    <!-- Order Header -->
    <div class="order-header-section mb-4">
        <div class="row">
            <div class="col-md-8">
                <div class="d-flex align-items-center">
                    <button class="btn btn-outline-secondary me-3" (click)="goBack()">
                        <i class="pi pi-arrow-left me-2"></i>
                        Back to Orders
                    </button>
                    <div>
                        <h2 class="order-title mb-1">{{ order.orderId }}</h2>
                        <p class="order-meta text-muted mb-0">
                            Created: {{ formatDate(order.createdAt) }}
                            <span *ngIf="order.updatedAt && order.updatedAt !== order.createdAt">
                                | Updated: {{ formatDate(order.updatedAt) }}
                            </span>
                        </p>
                    </div>
                </div>
            </div>
            <div class="col-md-4">
                <div class="d-flex justify-content-end align-items-center gap-2">
                    <p-tag 
                        [value]="getStatusLabel(order.orderStatus)" 
                        [severity]="getStatusSeverity(order.orderStatus)"
                        styleClass="p-tag-lg">
                    </p-tag>
                    <div *ngIf="permissionService.hasPermission('ORDER', 'd')" 
                         class="btn-group">
                        <button class="btn btn-outline-danger" 
                                (click)="deleteOrder()"
                                title="Delete Order">
                            <i class="pi pi-trash"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <div class="row">
        <!-- Main Content -->
        <div class="col-lg-8">
            <!-- Customer Information -->
            <div class="detail-card mb-4">
                <div class="card-header">
                    <h4 class="card-title">
                        <i class="pi pi-user me-2"></i>
                        Customer Information
                    </h4>
                </div>
                <div class="card-body">
                    <div class="row">
                        <div class="col-md-6">
                            <div class="info-item">
                                <label class="info-label">Name</label>
                                <p class="info-value">{{ order.customer.name }}</p>
                            </div>
                            <div class="info-item">
                                <label class="info-label">Email</label>
                                <p class="info-value email-with-copy">
                                    <a [href]="'mailto:' + order.customer.email">
                                        {{ order.customer.email }}
                                    </a>
                                    <button class="copy-email-btn" 
                                            (click)="copyEmailToClipboard(order.customer.email)"
                                            title="Copy Email">
                                        <i class="pi pi-copy"></i>
                                    </button>
                                </p>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="info-item" *ngIf="order.customer.phone">
                                <label class="info-label">Phone</label>
                                <p class="info-value">
                                    <a [href]="'tel:' + order.customer.phone">
                                        {{ order.customer.phone }}
                                    </a>
                                </p>
                            </div>
                            <div class="info-item" *ngIf="order.customer.organization">
                                <label class="info-label">Organization</label>
                                <p class="info-value">{{ order.customer.organization }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Product Information -->
            <div class="detail-card mb-4">
                <div class="card-header">
                    <h4 class="card-title">
                        <i class="pi pi-shopping-cart me-2"></i>
                        Product Information
                    </h4>
                </div>
                <div class="card-body">
                    <div class="row">
                        <div class="col-md-8">
                            <div class="info-item">
                                <label class="info-label">Product Name</label>
                                <p class="info-value">{{ order.productService.productName }}</p>
                            </div>
                            <div class="info-item" *ngIf="order.productService.productType">
                                <label class="info-label">Product Type</label>
                                <p class="info-value">{{ order.productService.productType }}</p>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="pricing-summary">
                                <div class="price-item">
                                    <span class="price-label">Unit Price:</span>
                                    <span class="price-value" [class.price-text]="!isNumericPrice(order.productService.unitPrice)">
                                        {{ formatCurrency(order.productService.unitPrice, order.productService.currency) }}
                                        <img src="assets/pdf/graphics/Saudi_Riyal_Symbol-1.png" alt="SAR" class="currency-icon-inline currency-14" />
                                    </span>
                                </div>
                                <div class="price-item" *ngIf="order.productService.vatAmount">
                                    <span class="price-label">VAT:</span>
                                    <span class="price-value" [class.price-text]="!isNumericPrice(order.productService.vatAmount)">
                                        {{ formatCurrency(order.productService.vatAmount, order.productService.currency) }}
                                        <img src="assets/pdf/graphics/Saudi_Riyal_Symbol-1.png" alt="SAR" class="currency-icon-inline currency-14" />
                                    </span>
                                </div>
                                <hr class="price-divider" *ngIf="isNumericPrice(order.productService.unitPrice) || isNumericPrice(order.productService.totalAmount)">
                                <div class="price-item total-price">
                                    <span class="price-label">Total Amount:</span>
                                    <span class="price-value" [class.price-text]="!isNumericPrice(order.productService.totalAmount)">
                                        {{ formatCurrency(order.productService.totalAmount, order.productService.currency) }}
                                        <img src="assets/pdf/graphics/Saudi_Riyal_Symbol-1.png" alt="SAR" class="currency-icon-inline currency-14" />
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Order Notes -->
            <div class="detail-card mb-4" *ngIf="order.orderNotes">
                <div class="card-header">
                    <h4 class="card-title">
                        <i class="pi pi-file-text me-2"></i>
                        Order Notes
                    </h4>
                </div>
                <div class="card-body">
                    <p class="order-notes">{{ order.orderNotes }}</p>
                </div>
            </div>

            <!-- Attachments -->
            <div class="detail-card mb-4" *ngIf="order.attachments && order.attachments.length > 0">
                <div class="card-header">
                    <h4 class="card-title">
                        <i class="pi pi-paperclip me-2"></i>
                        Attachments
                    </h4>
                </div>
                <div class="card-body">
                    <div class="attachments-list">
                        <div class="attachment-item" *ngFor="let attachment of order.attachments">
                            <div class="attachment-info">
                                <i class="pi pi-file me-2"></i>
                                <span class="attachment-name">{{ attachment.fileName }}</span>
                                <span class="attachment-type">{{ attachment.fileType }}</span>
                            </div>
                            <div class="attachment-actions">
                                <button class="btn btn-sm btn-outline-primary" 
                                        (click)="downloadAttachment(attachment)">
                                    <i class="pi pi-download me-1"></i>
                                    Download
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Order History -->
            <div class="detail-card mb-4" *ngIf="order.orderHistory && order.orderHistory.length > 0">
                <div class="card-header">
                    <h4 class="card-title">
                        <i class="pi pi-history me-2"></i>
                        Order History
                    </h4>
                </div>
                <div class="card-body">
                    <div class="timeline">
                        <div class="timeline-item" *ngFor="let history of order.orderHistory">
                            <div class="timeline-marker">
                                <p-tag 
                                    [value]="getStatusLabel(history.status)" 
                                    [severity]="getStatusSeverity(history.status)"
                                    styleClass="p-tag-sm">
                                </p-tag>
                            </div>
                            <div class="timeline-content">
                                <div class="timeline-header">
                                    <h6 class="timeline-title">{{ getStatusLabel(history.status) }}</h6>
                                    <span class="timeline-date">{{ formatDate(history.timestamp) }}</span>
                                </div>
                                <div class="timeline-body" *ngIf="history.notes">
                                    <p class="timeline-notes">{{ history.notes }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sidebar -->
        <div class="col-lg-4">
            <!-- Quick Actions -->
            <div class="detail-card mb-4">
                <div class="card-header">
                    <h4 class="card-title">
                        <i class="pi pi-cog me-2"></i>
                        Quick Actions
                    </h4>
                </div>
                <div class="card-body">
                    <div class="action-buttons">
                        <button *ngIf="permissionService.hasPermission('ORDER', 'u')" 
                                class="btn btn-primary w-100 mb-2" 
                                (click)="openStatusUpdateDialog()">
                            <i class="pi pi-pencil me-2"></i>
                            Update Status
                        </button>
                        <button *ngIf="permissionService.hasPermission('ORDER', 'u')" 
                                class="btn btn-outline-secondary w-100 mb-2" 
                                (click)="openCommentDialog()">
                            <i class="pi pi-comment me-2"></i>
                            Add Comment
                        </button>
                        <button *ngIf="permissionService.hasPermission('ORDER', 'u')" 
                                class="btn btn-outline-info w-100 mb-2" 
                                (click)="openAttachmentDialog()">
                            <i class="pi pi-paperclip me-2"></i>
                            Add Attachment
                        </button>
                    </div>
                </div>
            </div>

            <!-- Order Summary -->
            <div class="detail-card">
                <div class="card-header">
                    <h4 class="card-title">
                        <i class="pi pi-info-circle me-2"></i>
                        Order Summary
                    </h4>
                </div>
                <div class="card-body">
                    <div class="summary-item">
                        <span class="summary-label">Order ID:</span>
                        <span class="summary-value">{{ order.orderId }}</span>
                    </div>
                    <div class="summary-item">
                        <span class="summary-label">Status:</span>
                        <p-tag 
                            [value]="getStatusLabel(order.orderStatus)" 
                            [severity]="getStatusSeverity(order.orderStatus)">
                        </p-tag>
                    </div>
                    <div class="summary-item">
                        <span class="summary-label">Created:</span>
                        <span class="summary-value">{{ formatDate(order.createdAt) }}</span>
                    </div>
                    <div class="summary-item" *ngIf="order.assignedTo">
                        <span class="summary-label">Assigned To:</span>
                        <span class="summary-value">{{ order.assignedTo }}</span>
                    </div>
                    <div class="summary-item">
                        <span class="summary-label">Total Amount:</span>
                        <span class="summary-value total-amount" [class.price-text]="!isNumericPrice(order.productService.totalAmount)">
                            {{ formatCurrency(order.productService.totalAmount, order.productService.currency) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<!-- Loading Spinner -->
<div class="d-flex justify-content-center" *ngIf="spinner">
    <p-progressSpinner [hidden]="!spinner" styleClass="w-4rem h-4rem pb-4" strokeWidth="8"
        fill="var(--surface-ground)" animationDuration=".5s"></p-progressSpinner>
</div>

<app-footer></app-footer>

<!-- Status Update Dialog -->
<p-dialog 
    [(visible)]="showStatusUpdateDialog" 
    [modal]="true" 
    [closable]="true" 
    [draggable]="false" 
    [resizable]="false"
    [style]="{width: '500px'}"
    header="Update Order Status"
    (onHide)="closeStatusUpdateDialog()">
    
    <div class="status-update-content" *ngIf="order">
        <!-- Order Info -->
        <div class="order-info mb-4">
            <h5>Order: {{ order.orderId }}</h5>
            <p class="text-muted">Customer: {{ order.customer.name }}</p>
            <p class="text-muted">Product: {{ order.productService.productName }}</p>
        </div>

        <!-- Status Selection -->
        <div class="form-group mb-3">
            <label for="statusSelect" class="form-label">New Status</label>
            <p-dropdown 
                id="statusSelect"
                [options]="[
                    { label: 'Pending', value: 'PENDING' },
                    { label: 'Accepted', value: 'ACCEPTED' },
                    { label: 'Update Required', value: 'UPDATE' },
                    { label: 'Rejected', value: 'REJECTED' },
                    { label: 'Canceled', value: 'CANCELED' }
                ]"
                [(ngModel)]="newStatus"
                optionLabel="label" 
                optionValue="value"
                placeholder="Select Status"
                class="w-100">
            </p-dropdown>
        </div>

        <!-- Notes -->
        <div class="form-group mb-3">
            <label for="statusNotes" class="form-label">Notes (Optional)</label>
            <textarea 
                id="statusNotes"
                pInputTextarea 
                [(ngModel)]="statusUpdateNotes"
                placeholder="Add any notes about this status change..."
                rows="3"
                class="w-100">
            </textarea>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end gap-2">
            <button type="button" 
                    class="btn btn-secondary" 
                    (click)="closeStatusUpdateDialog()">
                Cancel
            </button>
            <button type="button" 
                    class="btn btn-primary" 
                    (click)="updateOrderStatus()">
                Update Status
            </button>
        </div>
    </div>
</p-dialog>

<!-- Comment Dialog -->
<p-dialog 
    [(visible)]="showCommentDialog" 
    [modal]="true" 
    [closable]="true" 
    [draggable]="false" 
    [resizable]="false"
    [style]="{width: '500px'}"
    header="Add Comment"
    (onHide)="closeCommentDialog()">
    
    <div class="comment-content">
        <div class="form-group mb-3">
            <label for="commentText" class="form-label">Comment</label>
            <textarea 
                id="commentText"
                pInputTextarea 
                [(ngModel)]="newComment"
                placeholder="Enter your comment..."
                rows="4"
                class="w-100">
            </textarea>
        </div>

        <div class="d-flex justify-content-end gap-2">
            <button type="button" 
                    class="btn btn-secondary" 
                    (click)="closeCommentDialog()">
                Cancel
            </button>
            <button type="button" 
                    class="btn btn-primary" 
                    (click)="addComment()"
                    [disabled]="!newComment.trim()">
                Add Comment
            </button>
        </div>
    </div>
</p-dialog>

<!-- Attachment Dialog -->
<p-dialog 
    [(visible)]="showAttachmentDialog" 
    [modal]="true" 
    [closable]="true" 
    [draggable]="false" 
    [resizable]="false"
    [style]="{width: '500px'}"
    header="Add Attachment"
    (onHide)="closeAttachmentDialog()">
    
    <div class="attachment-content">
        <div class="form-group mb-3">
            <label for="attachmentPath" class="form-label">Attachment Path/URL</label>
            <input 
                id="attachmentPath"
                type="text" 
                pInputText 
                [(ngModel)]="newAttachment"
                placeholder="Enter file path or URL..."
                class="w-100" />
        </div>

        <div class="d-flex justify-content-end gap-2">
            <button type="button" 
                    class="btn btn-secondary" 
                    (click)="closeAttachmentDialog()">
                Cancel
            </button>
            <button type="button" 
                    class="btn btn-primary" 
                    (click)="addAttachment()"
                    [disabled]="!newAttachment.trim()">
                Add Attachment
            </button>
        </div>
    </div>
</p-dialog>

<p-confirmDialog></p-confirmDialog>

<p-toast></p-toast>
